/**
 * DROP TABLE / TRUNCATE for the DB Manager: the confirmation modal and the SQL
 * it runs (owner, 30.09.2026: "DROP / Truncate should open a small modal where
 * these things can be turned on or off so the user is aware").
 *
 * Injected into the guest via toString (see treeRefresh.js), so each function
 * is self-contained ES5 — no closures over module scope, no imports.
 *
 * Before: one "Drop / truncate table" item, a select defaulting to DROP, and a
 * table name that was compared only after Execute — the modal closed with an
 * error toast and the user started over. No row count, no references, no
 * CASCADE / RESTART IDENTITY, and SQLite's "truncate" (DELETE FROM) never
 * reset AUTOINCREMENT.
 *
 * input: { kind: 'drop' | 'truncate', engine: 'postgres' | 'mysql' | 'sqlite',
 *          connName, dbName, tableName,
 *          rowEstimate: number | null   (null / negative = unknown),
 *          rowsExact: bool              (SQLite counts, the others estimate),
 *          referencedBy: [string]       ("orders.customer_id (fk_orders_customer)") }
 */
export function destructiveTableModal(input) {
  var drop = input.kind === 'drop';
  var engine = input.engine;
  var refs = input.referencedBy || [];
  var n = input.rowEstimate;
  var rows;
  // eslint-disable-next-line no-self-compare -- NaN check; plain ES5 (injected via toString)
  if (typeof n !== 'number' || n < 0 || n !== n) rows = 'unknown';
  else if (input.rowsExact) rows = String(n) + (n === 1 ? ' row' : ' rows');
  else rows = 'about ' + String(Math.round(n)) + (Math.round(n) === 1 ? ' row' : ' rows') + ' (estimate)';

  var where = [];
  if (input.connName) where.push('Connection: ' + input.connName);
  if (input.dbName) where.push((engine === 'postgres' ? 'Schema: ' : 'Database: ') + input.dbName);
  where.push('Table: ' + input.tableName);

  var fields = [
    {
      id: 'what',
      type: 'info',
      tone: drop ? 'danger' : 'warning',
      text: drop
        ? 'Deletes the table itself: its structure, indexes, triggers and all of its data. This cannot be undone.'
        : 'Removes every row but keeps the table, its columns and indexes. This cannot be undone.',
    },
    { id: 'where', type: 'info', text: where.join(' · ') },
    { id: 'rows', type: 'info', text: 'Data: ' + rows },
  ];

  if (refs.length) {
    var shown = refs.slice(0, 5).join(', ') + (refs.length > 5 ? ' and ' + (refs.length - 5) + ' more' : '');
    var refText = 'Referenced by: ' + shown + '.';
    if (engine === 'mysql' && !drop) refText += ' MySQL refuses TRUNCATE while a foreign key points at this table (error 1701).';
    else if (engine === 'mysql' && drop) refText += ' MySQL refuses DROP while a foreign key points at this table (error 3730).';
    else if (engine === 'postgres') refText += ' Without CASCADE, Postgres refuses.';
    fields.push({ id: 'refs', type: 'info', tone: 'warning', text: refText });
  }

  if (engine === 'postgres' && !drop) {
    fields.push({
      id: 'restartIdentity',
      type: 'checkbox',
      label: 'Restart identity (RESTART IDENTITY)',
      hint: 'Serial / identity columns start again from 1.',
      defaultValue: true,
    });
  }
  if (engine === 'postgres') {
    fields.push({
      id: 'cascade',
      type: 'checkbox',
      label: 'Cascade (CASCADE)',
      hint: drop
        ? 'Also drops views and foreign keys that depend on this table.'
        : 'Also empties every table that references this one — their rows are deleted too.',
      defaultValue: false,
    });
  }
  if (engine === 'sqlite' && !drop) {
    fields.push({
      id: 'resetAutoincrement',
      type: 'checkbox',
      label: 'Reset AUTOINCREMENT',
      hint: 'New rows start numbering from 1 again (removes the table from sqlite_sequence).',
      defaultValue: true,
    });
  }
  if (drop) {
    fields.push({
      id: 'confirmName',
      type: 'text',
      label: 'Type the table name to confirm',
      placeholder: input.tableName,
      required: true,
      mustEqual: input.tableName,
    });
  }

  return {
    title: drop ? 'Drop table' : 'Truncate table',
    subtitle: '"' + input.tableName + '"',
    submitLabel: drop ? 'Drop table' : 'Truncate',
    submitTone: 'danger',
    fields: fields,
  };
}

/**
 * The statements to run, in order: [{ sql, params }].
 * input: { kind, engine, ref (the quoted, qualified identifier), tableName }
 * options: the modal's answer ({ cascade, restartIdentity, resetAutoincrement }).
 */
export function destructiveTableSql(input, options) {
  var o = options || {};
  var drop = input.kind === 'drop';
  var engine = input.engine;
  if (engine === 'sqlite') {
    if (drop) return [{ sql: 'DROP TABLE ' + input.ref, params: [] }];
    var out = [{ sql: 'DELETE FROM ' + input.ref, params: [] }];
    // sqlite_sequence exists only once some table uses AUTOINCREMENT; the
    // caller skips a "no such table" error from this statement.
    if (o.resetAutoincrement) out.push({ sql: 'DELETE FROM sqlite_sequence WHERE name = ?', params: [input.tableName] });
    return out;
  }
  if (engine === 'postgres') {
    var sql = (drop ? 'DROP TABLE ' : 'TRUNCATE TABLE ') + input.ref;
    if (!drop && o.restartIdentity) sql += ' RESTART IDENTITY';
    if (o.cascade) sql += ' CASCADE';
    return [{ sql: sql, params: [] }];
  }
  // MySQL: TRUNCATE already resets AUTO_INCREMENT; there is no CASCADE.
  return [{ sql: (drop ? 'DROP TABLE ' : 'TRUNCATE TABLE ') + input.ref, params: [] }];
}

/**
 * What the modal needs to know first: how much data, and who points at the
 * table. { rows: { sql, params }, refs: { sql, params } | null }.
 * Postgres / MySQL estimate from the catalog (instant on any size); SQLite
 * counts, capped at 100,000 so a huge file cannot stall the modal.
 * Self-references are left out: they never block a DROP or TRUNCATE.
 */
export function destructiveTableQueries(engine, dbName, tableName, ref) {
  if (engine === 'postgres') {
    return {
      rows: {
        sql: 'SELECT c.reltuples::bigint FROM pg_class c JOIN pg_namespace n ON n.oid = c.relnamespace WHERE n.nspname = $1 AND c.relname = $2',
        params: [dbName, tableName],
      },
      refs: {
        sql:
          'SELECT DISTINCT cl.relname, con.conname FROM pg_constraint con ' +
          'JOIN pg_class cl ON cl.oid = con.conrelid ' +
          'JOIN pg_class fcl ON fcl.oid = con.confrelid ' +
          'JOIN pg_namespace fns ON fns.oid = fcl.relnamespace ' +
          "WHERE con.contype = 'f' AND fns.nspname = $1 AND fcl.relname = $2 AND con.conrelid <> con.confrelid " +
          'ORDER BY 1, 2',
        params: [dbName, tableName],
      },
    };
  }
  if (engine === 'mysql') {
    return {
      rows: { sql: 'SELECT TABLE_ROWS FROM information_schema.TABLES WHERE TABLE_SCHEMA = ? AND TABLE_NAME = ?', params: [dbName, tableName] },
      refs: {
        sql:
          'SELECT DISTINCT TABLE_NAME, CONSTRAINT_NAME FROM information_schema.KEY_COLUMN_USAGE ' +
          'WHERE REFERENCED_TABLE_SCHEMA = ? AND REFERENCED_TABLE_NAME = ? ' +
          'AND NOT (TABLE_SCHEMA = REFERENCED_TABLE_SCHEMA AND TABLE_NAME = REFERENCED_TABLE_NAME) ' +
          'ORDER BY 1, 2',
        params: [dbName, tableName],
      },
    };
  }
  return { rows: { sql: 'SELECT COUNT(*) FROM (SELECT 1 FROM ' + ref + ' LIMIT 100001)', params: [] }, refs: null };
}

/** Turn the two answers into the modal input's rowEstimate / rowsExact / referencedBy. */
export function destructiveTableContext(engine, rowsRes, refsRes) {
  var first = rowsRes && rowsRes.rows && rowsRes.rows[0] ? rowsRes.rows[0][0] : null;
  var n = first === null || first === undefined || first === '' ? null : Number(first);
  // eslint-disable-next-line no-self-compare -- NaN check; plain ES5 (injected via toString)
  if (n !== null && n !== n) n = null;
  var exact = engine === 'sqlite';
  // SQLite's count is capped at 100001: past the cap it is a lower bound.
  if (exact && n !== null && n > 100000) {
    n = 100000;
    exact = false;
  }
  var refs = [];
  var rows = (refsRes && refsRes.rows) || [];
  for (var i = 0; i < rows.length; i++) refs.push(rows[i][0] + ' (' + rows[i][1] + ')');
  return { rowEstimate: n, rowsExact: exact, referencedBy: refs };
}
